import { Calendar, FileText, Loader2, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { typography } from '../../styles/typography';
import { Card, IconButton, PrimaryButton } from '../ui';
import { getAllSites } from '../SiteManagement/siteApi';
import { fdocApi } from './FdocApi';

const resolveSiteValue = (site) => {
  return (
    site?.site_id ?? site?.siteId ?? site?.id ?? site?._id ?? site?.site_code ?? site?.siteCode ?? ''
  );
};

const FdocInPreview = ({ isOpen, onClose, documentData }) => {
  const [fullDocument, setFullDocument] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [error, setError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [editedExtractedData, setEditedExtractedData] = useState(null);
  const [applyEsign, setApplyEsign] = useState(false);
  const [selectedSignatoryId, setSelectedSignatoryId] = useState('');
  const [sites, setSites] = useState([]);
  const [versions, setVersions] = useState([]);
  const [versionsLastKey, setVersionsLastKey] = useState(null);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState('');
  const [snapshotDocument, setSnapshotDocument] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotError, setSnapshotError] = useState('');
  const confirmRetryRef = useRef(0);

  // Use fullDocument if available, otherwise fall back to documentData
  const latestDoc = fullDocument || documentData;
  const doc = snapshotDocument || latestDoc;
  const isSnapshotView = Boolean(snapshotDocument);
  const extractedData = doc?.extracted_data || latestDoc?.extracted_data || documentData?.extracted_data;
  // Use edited data if available, otherwise use original extracted data
  const displayExtractedData = isSnapshotView ? extractedData : (editedExtractedData || extractedData);

  // Fetch sites (for site_id -> site_name mapping)
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await getAllSites();
        const sitesList = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
            ? response
            : Array.isArray(response?.data)
              ? response.data
              : [];

        const sorted = [...sitesList].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );

        setSites(sorted.length > 0 ? sorted : []);
      } catch (err) {
        console.warn('[FdocInPreview] Error fetching sites:', err);
        setSites([]);
      }
    };

    if (isOpen) fetchSites();
  }, [isOpen]);

  // Fetch full document details when component opens
  useEffect(() => {
    if (isOpen && documentData?.fdoc_id) {
      fetchFullDocument();
      setEditedExtractedData(null); // Reset edited data when opening
      setApplyEsign(false);
      setSelectedSignatoryId('');
      setSnapshotDocument(null);
      confirmRetryRef.current = 0;
    }
  }, [isOpen, documentData?.fdoc_id]);

  const fetchVersions = async ({ reset = false } = {}) => {
    if (!documentData?.fdoc_id) return;
    if (versionsLoading) return;

    setVersionsLoading(true);
    setVersionsError('');
    try {
      const resp = await fdocApi.listVersions(documentData.fdoc_id, {
        limit: 50,
        last_key: reset ? null : versionsLastKey,
      });
      const newItems = Array.isArray(resp?.items) ? resp.items : [];
      setVersions((prev) => (reset ? newItems : [...prev, ...newItems]));
      setVersionsLastKey(resp?.last_key ?? null);
    } catch (e) {
      setVersionsError(e.message || 'Failed to load version history.');
    } finally {
      setVersionsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    if (!documentData?.fdoc_id) return;
    fetchVersions({ reset: true });
  }, [isOpen, documentData?.fdoc_id]);

  const openSnapshot = async (versionNo) => {
    if (!documentData?.fdoc_id) return;
    setSnapshotLoading(true);
    setSnapshotError('');
    setError('');
    try {
      const snap = await fdocApi.getVersionSnapshot(documentData.fdoc_id, versionNo);
      setSnapshotDocument(snap);
      setEditedExtractedData(null);
    } catch (e) {
      setSnapshotError(e.message || 'Failed to load version snapshot.');
    } finally {
      setSnapshotLoading(false);
    }
  };

  const backToLatest = () => {
    setSnapshotDocument(null);
    setSnapshotError('');
    setError('');
  };

  // Initialize editedExtractedData when extractedData changes
  useEffect(() => {
    if (!isOpen) return;

    const inferredType =
      extractedData?.type ||
      doc?.document_type ||
      documentData?.document_type ||
      doc?.type ||
      documentData?.type ||
      '';
    const inferredDate =
      extractedData?.date ||
      doc?.document_date ||
      documentData?.document_date ||
      (doc?.uploaded_at ? new Date(doc.uploaded_at).toISOString().split('T')[0] : '') ||
      '';

    // Ensure the editable extracted data is pre-filled in the form (not just shown as fallback text).
    if ((extractedData || inferredType || inferredDate) && !editedExtractedData) {
      const base = extractedData ? JSON.parse(JSON.stringify(extractedData)) : { type: '', date: '', fields: {} };
      if (!base.type && inferredType) base.type = inferredType;
      if (!base.date && inferredDate) base.date = inferredDate;
      setEditedExtractedData(base);
      return;
    }

    // If editedExtractedData exists but is missing these basics, auto-fill once.
    if (editedExtractedData) {
      const next = { ...editedExtractedData };
      let changed = false;
      if (!next.type && inferredType) {
        next.type = inferredType;
        changed = true;
      }
      if (!next.date && inferredDate) {
        next.date = inferredDate;
        changed = true;
      }
      if (changed) setEditedExtractedData(next);
    }
  }, [isOpen, extractedData, editedExtractedData, doc, documentData]);

  const fetchFullDocument = async () => {
    if (!documentData?.fdoc_id) return;

    setIsLoading(true);
    setError('');

    try {
      const doc = await fdocApi.getDocument(documentData.fdoc_id);
      setFullDocument(doc);
    } catch (err) {
      console.error('Error fetching full document:', err);
      setError(err.message || 'Failed to load document details.');
      // Use the documentData we already have as fallback
      setFullDocument(documentData);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return `₹${parseFloat(amount).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  // Format field name for display
  const formatFieldName = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const isObjectLikeValue = (value) =>
    value !== null && typeof value === 'object';

  const normalizeOcrNumericText = (value) => {
    if (typeof value !== 'string') return value;
    const superscriptMap = {
      '⁰': '0',
      '¹': '1',
      '²': '2',
      '³': '3',
      '⁴': '4',
      '⁵': '5',
      '⁶': '6',
      '⁷': '7',
      '⁸': '8',
      '⁹': '9',
      '₀': '0',
      '₁': '1',
      '₂': '2',
      '₃': '3',
      '₄': '4',
      '₅': '5',
      '₆': '6',
      '₇': '7',
      '₈': '8',
      '₉': '9',
    };

    const replaced = value.replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹₀₁₂₃₄₅₆₇₈₉]/g, (ch) => superscriptMap[ch] || ch);

    // Clean up values that are only numeric/currency punctuation after OCR normalization.
    const compact = replaced.replace(/\s+/g, ' ').trim();
    const numericLike = /^[₹rs\.\s\d,%-]+$/i.test(compact);
    return numericLike ? compact.replace(/\s+/g, '') : compact;
  };

  const getEditableFieldValue = (value) => {
    if (value === null || value === undefined) return '';
    if (isObjectLikeValue(value)) {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
    return String(normalizeOcrNumericText(value));
  };

  const parseEditedFieldValue = (rawValue, previousValue) => {
    const trimmed = rawValue.trim();
    if (isObjectLikeValue(previousValue)) {
      if (!trimmed) return null;
      try {
        return JSON.parse(rawValue);
      } catch {
        return rawValue;
      }
    }
    if (typeof previousValue === 'number') {
      if (trimmed === '') return '';
      const numValue = parseFloat(rawValue);
      return Number.isNaN(numValue) ? rawValue : numValue;
    }
    if (typeof previousValue === 'boolean') {
      if (trimmed.toLowerCase() === 'true') return true;
      if (trimmed.toLowerCase() === 'false') return false;
    }
    return rawValue;
  };

  const normalizeFieldKey = (label) =>
    String(label || '')
      .trim()
      .toLowerCase()
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, '_');

  const parseTextBlocksToFields = (textBlocks) => {
    const parsed = {};
    if (!Array.isArray(textBlocks)) return parsed;

    const sectionLabelPatterns = [
      { regex: /^vendor(?:\/customer)?\s+details?$/i, key: 'vendor_customer_details' },
      { regex: /^vendor(?:\s+name)?$/i, key: 'vendor_details' },
      { regex: /^customer(?:\s+name)?$/i, key: 'customer_details' },
      { regex: /^expected\s+delivery$/i, key: 'expected_delivery' },
      { regex: /^date\s+of\s+delivery$/i, key: 'date_of_delivery' },
      { regex: /^date\s+created$/i, key: 'date_created' },
      { regex: /^date\s+of\s+expiry$/i, key: 'date_of_expiry' },
      {
        regex: /^(bill\s+of\s+materials?|contents?|itemized\s+list)$/i,
        key: 'bill_of_materials',
      },
      { regex: /^materials?(?:\/items?)?$/i, key: 'materials_items' },
      { regex: /^items?$/i, key: 'itemized_list' },
    ];

    const lines = textBlocks
      .flatMap((block) => (typeof block === 'string' ? block.split('\n') : []))
      .map((line) => line.trim())
      .filter(Boolean);

    const isSectionHeading = (value) =>
      sectionLabelPatterns.some(({ regex }) => regex.test(value)) ||
      /^(order details|items|summary)$/i.test(value);
    const isLabelLikeLine = (value) => /:\s*$/.test(value) || /^[A-Za-z ]+:\s*.+$/.test(value);

    let currentSectionKey = '';
    const collectedItems = [];

    lines.forEach((line, index) => {
      const matchedSection = sectionLabelPatterns.find(({ regex }) => regex.test(line));
      if (matchedSection) {
        currentSectionKey = matchedSection.key;
      }

      const colonIndex = line.indexOf(':');
      if (colonIndex > 0) {
        const rawLabel = line.slice(0, colonIndex).trim();
        const rawValue = line.slice(colonIndex + 1).trim();
        const normalizedLabelKey = normalizeFieldKey(rawLabel);

        // Handle label with value on next line: "Name:" then "sneha sree"
        const resolvedValue = rawValue || String(lines[index + 1] || '').trim();
        if (normalizedLabelKey && resolvedValue) {
          if (parsed[normalizedLabelKey] === undefined || parsed[normalizedLabelKey] === '') {
            parsed[normalizedLabelKey] = resolvedValue;
          }

          // Explicit mapping for section "Name" fields.
          if (normalizedLabelKey === 'name') {
            if (
              currentSectionKey === 'vendor_details' &&
              (parsed.vendor_name === undefined || parsed.vendor_name === '')
            ) {
              parsed.vendor_name = resolvedValue;
            }
            if (
              (currentSectionKey === 'customer_details' ||
                currentSectionKey === 'vendor_customer_details') &&
              (parsed.customer_name === undefined || parsed.customer_name === '')
            ) {
              parsed.customer_name = resolvedValue;
            }
          }
        }
        return;
      }

      // Fallback: OCR often gives "section heading" on one line and value on next line.
      if (matchedSection) {
        const nextLine = lines[index + 1];
        const nextToNextLine = lines[index + 2];

        // Collect all items lines under ITEMS until next heading/summary.
        if (matchedSection.key === 'itemized_list') {
          let cursor = index + 1;
          while (cursor < lines.length) {
            const candidate = String(lines[cursor] || '').trim();
            if (!candidate) {
              cursor += 1;
              continue;
            }
            if (isSectionHeading(candidate) || /^thank you/i.test(candidate) || /^visit us/i.test(candidate)) {
              break;
            }
            // Keep product lines, skip qty/price/subtotal label rows
            if (!isLabelLikeLine(candidate) && !/^(rs\.?|qty|price|subtotal|total|cgst|sgst|igst)\b/i.test(candidate)) {
              if (!collectedItems.includes(candidate)) {
                collectedItems.push(candidate);
              }
            }
            cursor += 1;
          }
          return;
        }

        if (nextLine && !sectionLabelPatterns.some(({ regex }) => regex.test(nextLine))) {
          // Handle "Name:" on one line and actual value on the following line
          const nameOnlyLabelMatch = nextLine.match(/^(name|vendor|customer)\s*:\s*$/i);
          const inlineLabelValueMatch = nextLine.match(/^(name|vendor|customer)\s*:\s*(.+)$/i);

          let resolvedValue = '';
          if (inlineLabelValueMatch?.[2]) {
            resolvedValue = inlineLabelValueMatch[2].trim();
          } else if (nameOnlyLabelMatch && nextToNextLine) {
            resolvedValue = String(nextToNextLine).trim();
          } else {
            resolvedValue = nextLine;
          }

          if (
            resolvedValue &&
            !sectionLabelPatterns.some(({ regex }) => regex.test(resolvedValue)) &&
            (parsed[matchedSection.key] === undefined || parsed[matchedSection.key] === '')
          ) {
            parsed[matchedSection.key] = resolvedValue;
          }

          if (
            matchedSection.key === 'vendor_customer_details' &&
            (parsed.customer_name === undefined || parsed.customer_name === '')
          ) {
            parsed.customer_name = resolvedValue;
          }
          if (
            matchedSection.key === 'vendor_details' &&
            (parsed.vendor_name === undefined || parsed.vendor_name === '')
          ) {
            parsed.vendor_name = resolvedValue;
          }
          if (
            matchedSection.key === 'customer_details' &&
            (parsed.customer_name === undefined || parsed.customer_name === '')
          ) {
            parsed.customer_name = resolvedValue;
          }
        }
        return;
      }

      // Fallback: "Expected delivery 12-03-2026" style (no colon)
      const compactMatch = line.match(
        /^(expected delivery|date of delivery|date created|date of expiry)\s+(.+)$/i
      );
      if (compactMatch) {
        const key = normalizeFieldKey(compactMatch[1]);
        const value = compactMatch[2]?.trim();
        if (key && value && (parsed[key] === undefined || parsed[key] === '')) {
          parsed[key] = value;
        }
      }
    });

    if (collectedItems.length > 0 && (!parsed.itemized_list || parsed.itemized_list === '')) {
      parsed.itemized_list = collectedItems.join(', ');
    }

    return parsed;
  };

  const getTextBlockCandidates = () => {
    const candidates = [];
    const pushIfArray = (value) => {
      if (Array.isArray(value)) candidates.push(value);
    };

    pushIfArray(displayExtractedData?.fields?.text_blocks);
    pushIfArray(extractedData?.fields?.text_blocks);
    pushIfArray(displayExtractedData?.text_blocks);
    pushIfArray(extractedData?.text_blocks);
    pushIfArray(doc?.extraction_result);
    pushIfArray(doc?.extraction_metadata?.extraction_result);
    pushIfArray(doc?.extraction_metadata?.text_blocks);

    return candidates;
  };

  const getCustomerFallbackValue = () => {
    const possibleValues = [
      displayExtractedData?.fields?.customer_name,
      displayExtractedData?.fields?.bill_to,
      displayExtractedData?.fields?.customer,
      displayExtractedData?.customer_name,
      extractedData?.fields?.customer_name,
      extractedData?.fields?.bill_to,
      extractedData?.fields?.customer,
      extractedData?.customer_name,
      doc?.customer_name,
      doc?.bill_to,
      doc?.customer,
      doc?.party_name,
      doc?.buyer_name,
      documentData?.customer_name,
      documentData?.bill_to,
      documentData?.customer,
      documentData?.party_name,
      documentData?.buyer_name,
    ];

    return possibleValues.find(
      (val) => typeof val === 'string' && val.trim() !== ''
    ) || '';
  };

  const getVendorFallbackValue = () => {
    const possibleValues = [
      displayExtractedData?.fields?.vendor_name,
      displayExtractedData?.fields?.vendor,
      displayExtractedData?.fields?.supplier_name,
      displayExtractedData?.fields?.supplier,
      displayExtractedData?.fields?.seller_name,
      displayExtractedData?.fields?.seller,
      displayExtractedData?.vendor_name,
      extractedData?.fields?.vendor_name,
      extractedData?.fields?.vendor,
      extractedData?.fields?.supplier_name,
      extractedData?.fields?.supplier,
      extractedData?.fields?.seller_name,
      extractedData?.fields?.seller,
      extractedData?.vendor_name,
      doc?.vendor_name,
      doc?.vendor,
      doc?.supplier_name,
      doc?.supplier,
      doc?.seller_name,
      doc?.seller,
      documentData?.vendor_name,
      documentData?.vendor,
      documentData?.supplier_name,
      documentData?.supplier,
      documentData?.seller_name,
      documentData?.seller,
    ];

    return possibleValues.find(
      (val) => typeof val === 'string' && val.trim() !== ''
    ) || '';
  };

  const getMergedExtractedFields = () => {
    const baseFields = displayExtractedData?.fields || {};
    const merged = { ...baseFields };
    const textBlockCandidates = getTextBlockCandidates();
    const currentDocType = getCurrentDocType();

    textBlockCandidates.forEach((blocks) => {
      const parsedFromTextBlocks = parseTextBlocksToFields(blocks);
      Object.entries(parsedFromTextBlocks).forEach(([k, v]) => {
        if (merged[k] === undefined || merged[k] === null || merged[k] === '') {
          merged[k] = v;
        }
      });
    });

    // Fallback: parse customer name from raw_text when OCR split parsing misses it.
    if (!merged.customer_name && typeof baseFields?.raw_text === 'string') {
      const rawText = baseFields.raw_text;
      const customerSectionMatch = rawText.match(
        /CUSTOMER DETAILS[\s\S]*?Name:\s*([^\n\r]+)/i
      );
      if (customerSectionMatch?.[1]?.trim()) {
        merged.customer_name = customerSectionMatch[1].trim();
      }
    }

    // Bill docs often extract "name" but not "customer_name".
    if (
      !merged.customer_name &&
      currentDocType.includes('bill') &&
      typeof merged.name === 'string' &&
      merged.name.trim() !== ''
    ) {
      merged.customer_name = merged.name.trim();
    }

    // Normalize customer field aliases so customer name is visible consistently in Extracted Details.
    if (!merged.customer_name) {
      merged.customer_name =
        merged.client_name ||
        merged.client ||
        merged.bill_to ||
        merged.customer ||
        merged.party_name ||
        merged.buyer_name ||
        getCustomerFallbackValue();
    }

    // Normalize vendor field aliases so vendor name is visible consistently in Extracted Details.
    if (!merged.vendor_name) {
      merged.vendor_name =
        merged.vendor_details ||
        merged.vendor ||
        merged.supplier_name ||
        merged.supplier ||
        merged.seller_name ||
        merged.seller ||
        getVendorFallbackValue();
    }

    return merged;
  };

  const getCurrentDocType = () =>
    String(displayExtractedData?.type || doc?.document_type || documentData?.document_type || '')
      .trim()
      .toLowerCase();

  const RAW_FIELD_KEY_PATTERNS = [
    'text_blocks',
    'raw_text',
    'ocr_text',
    'full_text',
    'plain_text',
    'line_items_text',
  ];

  const EXCLUDED_EXTRACTED_FIELD_PATTERNS = [
    /^qty$/,
    /^quantity$/,
    /^subtotal$/,
    /^cgst(?:_?\d+)?$/,
    /^sgst(?:_?\d+)?$/,
    /^igst(?:_?\d+)?$/,
    /^cgst_?\(?\d+%?\)?$/,
    /^sgst_?\(?\d+%?\)?$/,
    /^igst_?\(?\d+%?\)?$/,
  ];

  const UNIVERSAL_RELEVANT_KEYWORDS = [
    'doc',
    'document',
    'serial',
    'number',
    'date',
    'expiry',
    'expiration',
    'delivery',
    'due',
    'vendor',
    'customer',
    'supplier',
    'buyer',
    'seller',
    'client',
    'party',
    'material',
    'item',
    'qty',
    'quantity',
    'unit',
    'amount',
    'tax',
    'gst',
    'cgst',
    'sgst',
    'igst',
    'subtotal',
    'total',
    'description',
    'address',
    'phone',
    'email',
    'terms',
  ];

  const DOC_TYPE_KEYWORDS = {
    po: ['po', 'purchase_order', 'expected_delivery', 'delivery_date', 'vendor', 'material', 'item'],
    invoice: ['invoice', 'due_date', 'bill_to', 'customer', 'client', 'tax', 'subtotal', 'total', 'amount'],
    bill: [
      'bill',
      'receipt',
      'payable',
      'amount',
      'tax',
      'total',
      'vendor',
      'customer',
      'customer_name',
      'bill_to',
      'party_name',
      'buyer',
    ],
    estimation: ['estimation', 'estimate', 'quotation', 'quote', 'valid_till', 'expiry', 'customer', 'item'],
    quotation: ['quotation', 'quote', 'valid_till', 'expiry', 'customer', 'item', 'amount'],
    reception: ['received', 'receipt', 'vendor', 'customer', 'date'],
  };

  const FIELD_LABEL_OVERRIDES = {
    doc_number: 'Doc Number',
    document_number: 'Doc Number',
    serial_no: 'Serial No',
    serial_number: 'Serial No',
    date_created: 'Date Created',
    created_date: 'Date Created',
    date_of_expiry: 'Date of Expiry',
    expiry_date: 'Date of Expiry',
    expected_delivery: 'Expected Delivery',
    date_of_delivery: 'Date of Delivery',
    delivery_date: 'Date of Delivery',
    vendor_customer_details: 'Vendor/Customer Details',
    customer_name: 'Customer Name',
    vendor_name: 'Vendor Name',
    vendor: 'Vendor Name',
    supplier_name: 'Vendor Name',
    supplier: 'Vendor Name',
    seller_name: 'Vendor Name',
    seller: 'Vendor Name',
    vendor_details: 'Vendor Name',
    client: 'Customer Name',
    client_name: 'Customer Name',
    bill_to: 'Bill To',
    party_name: 'Customer Name',
    buyer_name: 'Customer Name',
    bill_of_materials: 'Bill of Materials',
    itemized_list: 'Itemized List',
  };

  const getFieldDisplayLabel = (key) => FIELD_LABEL_OVERRIDES[key] || formatFieldName(key);

  const isRawExtractedFieldKey = (key) =>
    RAW_FIELD_KEY_PATTERNS.some((pattern) => key.includes(pattern));

  const isRelevantFieldForDocType = (key, docType) => {
    if (isRawExtractedFieldKey(key)) return false;
    if (EXCLUDED_EXTRACTED_FIELD_PATTERNS.some((pattern) => pattern.test(key))) return false;

    const normalizedKey = String(key || '').toLowerCase();
    const commonMatch = UNIVERSAL_RELEVANT_KEYWORDS.some((kw) => normalizedKey.includes(kw));

    if (!docType) return commonMatch;

    const typeKeywords = Object.entries(DOC_TYPE_KEYWORDS).find(([type]) =>
      docType.includes(type)
    )?.[1] || [];

    const typeMatch = typeKeywords.some((kw) => normalizedKey.includes(kw));
    return commonMatch || typeMatch;
  };

  const getRelevantExtractedFields = () => {
    const merged = getMergedExtractedFields();
    const docType = getCurrentDocType();
    const fieldPriority = [
      'customer_name',
      'vendor_name',
      'invoice_number',
      'po_number',
      'quotation_number',
      'bill_number',
      'date',
      'total_amount',
      'tax_total',
    ];
    const getFieldPriority = (key) => {
      const idx = fieldPriority.indexOf(String(key || '').toLowerCase());
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };

    const relevantEntries = Object.entries(merged)
      .filter(([fieldKey, fieldValue]) => {
        if (fieldValue === null || fieldValue === undefined || fieldValue === '') return false;
        return isRelevantFieldForDocType(fieldKey, docType);
      })
      .sort(([a], [b]) => a.localeCompare(b));

    // Deduplicate aliases that render to the same UI label
    // (e.g. customer_name/client/party_name => "Customer Name").
    const byDisplayLabel = new Map();
    relevantEntries.forEach(([fieldKey, fieldValue]) => {
      const labelKey = getFieldDisplayLabel(fieldKey).toLowerCase().trim();
      const existing = byDisplayLabel.get(labelKey);
      if (!existing) {
        byDisplayLabel.set(labelKey, [fieldKey, fieldValue]);
        return;
      }
      const existingPriority = getFieldPriority(existing[0]);
      const currentPriority = getFieldPriority(fieldKey);
      if (currentPriority < existingPriority) {
        byDisplayLabel.set(labelKey, [fieldKey, fieldValue]);
      }
    });

    return Array.from(byDisplayLabel.values()).sort(([a], [b]) => a.localeCompare(b));
  };

  // Format field value for display
  const formatFieldValue = (key, value) => {
    if (value === null || value === undefined) return 'N/A';
    
    // Handle date fields
    if (key.includes('date') || key.includes('_at') || key === 'date') {
      return formatDate(value);
    }
    
    // Handle boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    // Handle objects and arrays (show as JSON string)
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    
    return String(value);
  };

  // Get all top-level fields from document (excluding nested objects)
  const getBasicInfoFields = (extractedDataForDisplay) => {
    if (!doc) return [];
    const mergedExtractedFields = getMergedExtractedFields();
    const hasSplitTaxValues =
      mergedExtractedFields?.cgst != null ||
      mergedExtractedFields?.sgst != null ||
      mergedExtractedFields?.igst != null;
    const resolvedTaxTotal =
      mergedExtractedFields?.tax_total ??
      mergedExtractedFields?.tax_amount ??
      (hasSplitTaxValues
        ? (Number(mergedExtractedFields?.cgst) || 0) +
          (Number(mergedExtractedFields?.sgst) || 0) +
          (Number(mergedExtractedFields?.igst) || 0)
        : null);
    
    const siteId = doc?.site_id ?? doc?.siteId ?? doc?.site?.site_id ?? doc?.site?.siteId ?? doc?.site?.id ?? null;
    const rawSiteName =
      doc?.site_name ??
      doc?.siteName ??
      doc?.site_code ??
      doc?.siteCode ??
      doc?.site?.site_name ??
      doc?.site?.siteName ??
      doc?.site?.site_code ??
      doc?.site?.siteCode ??
      doc?.site?.name ??
      null;
    const matchedSite =
      siteId != null && sites.length > 0
        ? sites.find(s => String(resolveSiteValue(s)) === String(siteId))
        : null;
    const resolvedFromSites = matchedSite?.site_name || matchedSite?.name || null;
    const safeRawSiteName =
      rawSiteName && siteId != null && String(rawSiteName) === String(siteId) ? null : rawSiteName;
    const siteDisplayName = safeRawSiteName || resolvedFromSites || null;
    const versionNoRaw = doc?.version_no ?? doc?.versionNo ?? null;
    const versionDisplay =
      versionNoRaw != null && versionNoRaw !== '' ? `v${versionNoRaw}` : 'N/A';
    const isConfirmed = doc?.status === 'confirmed' || doc?.apiStatus === 'confirmed';

    // Exclude nested objects and fields that should not be displayed
    const excludedKeys = [
      'extracted_data', 
      'extraction_metadata',
      'tenant_id',
      'tenantId',
      'site_id',
      'siteId',
      'site_name',
      'siteName',
      'site_code',
      'siteCode',
      'selected_site_id',
      'selectedSiteId',
      'version_no',
      'versionNo',
      's3_key_temp',
      's3KeyTemp',
      's3_key',
      's3Key',
      'created_by',
      'createdBy',
      'file_type',
      'fileType',
      ...(isConfirmed
        ? [
            'esign_signature',
            'esignSignature',
            'esign_signature_algorithm',
            'esignSignatureAlgorithm',
            'esign_signing_key_id',
            'esignSigningKeyId',
            's3_key_permanent',
            's3KeyPermanent',
          ]
        : []),
    ];
    const fields = [];
    
    // Add known fields first in a specific order
    const knownFields = [
      { key: 'document_date', label: 'Document Date', value: extractedDataForDisplay?.date || doc?.document_date, isDate: true, isEditable: true },
      { key: 'document_type', label: 'Document Type', value: extractedDataForDisplay?.type || doc?.document_type, isEditable: true },
      { key: 'site', label: 'Site', value: siteDisplayName || 'N/A' },
      { key: 'version', label: 'Version', value: versionDisplay },
      {
        key: 'total_amount',
        label: 'Total Amount',
        value:
          mergedExtractedFields?.total_amount ??
          mergedExtractedFields?.grand_total ??
          mergedExtractedFields?.amount ??
          doc?.totalAmount ??
          doc?.total_amount ??
          null,
      },
      {
        key: 'tax_total',
        label: 'Tax Amount',
        value:
          resolvedTaxTotal ??
          doc?.taxAmount ??
          doc?.tax_total ??
          null,
      },
      { key: 'docNumber', label: 'Document Number', value: doc?.docNumber || `FDOC-${doc?.fdoc_id?.substring(0, 8)?.toUpperCase() || 'N/A'}` },
      {
        key: 'vendor_name',
        label: 'Vendor Name',
        value:
          mergedExtractedFields?.vendor_name ||
          mergedExtractedFields?.vendor ||
          mergedExtractedFields?.supplier_name ||
          mergedExtractedFields?.supplier ||
          mergedExtractedFields?.seller_name ||
          mergedExtractedFields?.seller ||
          doc?.vendor_name ||
          doc?.vendor ||
          null,
      },
      { key: 'fdoc_id', label: 'Document ID', value: doc?.fdoc_id },
      { key: 'status', label: 'Status', value: doc?.status || doc?.apiStatus },
      { key: 'uploaded_at', label: 'Upload Date', value: doc?.date || doc?.uploaded_at, isDate: true },
      { key: 'file_name', label: 'File Name', value: doc?.file_name },
    ];
    
    knownFields.forEach(field => {
      // For document_date, show it if extracted data exists (even if date is empty, user can set it)
      if (field.key === 'document_date') {
        if (extractedDataForDisplay || doc?.extracted_data) {
          fields.push(field);
        }
      } else if (field.value !== undefined && field.value !== null) {
        fields.push(field);
      }
    });
    
    // Add all other top-level fields (excluding the excluded keys)
    Object.keys(doc).forEach(key => {
      const lowerKey = key.toLowerCase();
      const isExcluded = excludedKeys.some(excluded => excluded.toLowerCase() === lowerKey);
      
      if (!isExcluded && typeof doc[key] !== 'object' && !knownFields.find(f => f.key === key)) {
        fields.push({
          key,
          label: formatFieldName(key),
          value: doc[key],
        });
      }
    });
    
    return fields;
  };

  // Handle extract document
  const handleExtract = async () => {
    if (!doc?.fdoc_id) {
      setError('Document ID is missing. Cannot extract.');
      return;
    }

    // Get s3_key from document - try different possible field names
    const s3Key = doc?.s3_key || doc?.object_key || doc?.s3_key_temp || doc?.s3_key_permanent;
    
    if (!s3Key) {
      setError('S3 key is missing. Cannot extract document. Please ensure the document has been uploaded.');
      return;
    }

    setIsExtracting(true);
    setError('');
    setSubmitSuccess(false);

    try {
      const result = await fdocApi.extract({
        fdoc_id: doc.fdoc_id,
        s3_key: s3Key,
      });

      setSubmitSuccess(true);
      setSuccessMessage('Document extracted successfully!');
      
      // Initialize edited data with extracted data
      if (result.extracted_data) {
        setEditedExtractedData(JSON.parse(JSON.stringify(result.extracted_data))); // Deep copy
      }
      
      // Refresh document data after successful extraction
      await fetchFullDocument();
      
      // Show success message for a few seconds
      setTimeout(() => {
        setSubmitSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error extracting document:', err);
      setError(err.message || 'Failed to extract document. Please try again.');
    } finally {
      setIsExtracting(false);
    }
  };

  // Handle field change in extracted data
  const handleExtractedDataChange = (path, value) => {
    setEditedExtractedData(prev => {
      if (!prev) return prev;
      const newData = JSON.parse(JSON.stringify(prev)); // Deep copy
      
      if (path === 'type') {
        newData.type = value;
      } else if (path === 'date') {
        newData.date = value;
      } else if (path.startsWith('fields.')) {
        const fieldKey = path.replace('fields.', '');
        if (!newData.fields) {
          newData.fields = {};
        }
        // Handle number conversion for numeric fields
        if (typeof value === 'string' && !isNaN(value) && value !== '') {
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            newData.fields[fieldKey] = numValue;
          } else {
            newData.fields[fieldKey] = value;
          }
        } else {
          newData.fields[fieldKey] = value;
        }
      }
      
      return newData;
    });
  };

  // Handle submit/confirm document
  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!latestDoc?.fdoc_id) {
      setError('Document ID is missing. Cannot submit.');
      return;
    }

    const dataToConfirm = editedExtractedData || extractedData;
    
    if (!dataToConfirm) {
      setError('No extracted data available. Cannot submit.');
      return;
    }

    const normalizedFields = Object.fromEntries(getRelevantExtractedFields());
    const finalDataToConfirm = {
      ...dataToConfirm,
      fields: normalizedFields,
    };

    setIsSubmitting(true);
    setError('');
    setSubmitSuccess(false);

    try {
      const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      let resp = null;
      let lastErr = null;
      const maxAttempts = 3;
      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
          resp = await fdocApi.confirm({
            fdoc_id: latestDoc.fdoc_id,
            extracted_data: finalDataToConfirm,
            apply_authorised_esign: applyEsign === true,
            signatory_id: selectedSignatoryId || undefined,
          });
          lastErr = null;
          break;
        } catch (err) {
          lastErr = err;
          if (err?.status === 409 && err?.code === 'version_conflict') {
            setError(
              attempt < maxAttempts
                ? 'Document was updated by another request. Refreshing and retrying…'
                : 'Document was updated by another request. Please try again.'
            );
            try {
              await fetchFullDocument();
              await fetchVersions({ reset: true });
            } catch {}
            // Backoff a bit to let backend settle (helps when multiple requests race).
            await sleep(350 * attempt);
            continue;
          }
          throw err;
        }
      }

      if (!resp && lastErr) {
        throw lastErr;
      }

      setSubmitSuccess(true);
      const isAlreadyConfirmed = latestDoc?.status === 'confirmed' || latestDoc?.apiStatus === 'confirmed';
      const versionNo = resp?.version_no;
      setSuccessMessage(
        isAlreadyConfirmed
          ? `Revision created${versionNo ? ` (Version #${versionNo})` : ''}!`
          : `Document confirmed${versionNo ? ` (Version #${versionNo})` : ''}!`
      );
      setEditedExtractedData(null); // Clear edited data after successful confirmation

      // Optimistically reflect status/version while backend propagates.
      setFullDocument((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          status: resp?.status ?? prev.status ?? 'confirmed',
          apiStatus: resp?.status ?? prev.apiStatus,
          version_no: resp?.version_no ?? prev.version_no,
          versionNo: resp?.version_no ?? prev.versionNo,
          confirmed_at: resp?.confirmed_at ?? prev.confirmed_at,
          esign_applied: resp?.esign_applied ?? prev.esign_applied,
          esign_signatory_id: resp?.esign_signatory_id ?? prev.esign_signatory_id,
          esign_signatory_name: resp?.esign_signatory_name ?? prev.esign_signatory_name,
          esign_signatory_designation:
            resp?.esign_signatory_designation ?? prev.esign_signatory_designation,
        };
      });
      
      // Refresh document data after successful confirmation
      await fetchFullDocument();
      await fetchVersions({ reset: true });

      // Close the preview after successful confirm/revision.
      if (typeof onClose === 'function') {
        onClose();
      }
      
      // Show success message for a few seconds
      setTimeout(() => {
        setSubmitSuccess(false);
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error confirming document:', err);
      if (err?.status === 409 && err?.code === 'version_conflict') {
        setError('Document was updated by another request. Please try again.');
        return;
      }
      if (applyEsign) {
        // Preserve the backend message (e.g. missing FDOC_KMS_KEY_ID) so issues can be fixed quickly.
        setError(err?.message || 'We could not apply authorised e-signature to this document.');
      } else {
        setError(err.message || 'Failed to confirm document. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const EsignStatus = ({ fdoc }) => {
    if (!fdoc?.esign_applied) return null;

    const name = fdoc.esign_signatory_name;
    const designation = fdoc.esign_signatory_designation;
    const appliedAt = fdoc.esign_applied_at;

    return (
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm font-semibold text-blue-900">Authorised e-signature</p>
        <p className="text-sm text-blue-800 mt-1">
          Signed
          {name ? ` by ${name}` : ''}
          {designation ? ` (${designation})` : ''}
          {appliedAt ? ` on ${new Date(appliedAt).toLocaleString()}` : ''}
          .
        </p>
      </div>
    );
  };

  return (
    <>
      {/* Dialog Box */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 overflow-y-auto">
        <div className="w-[95%] sm:w-[90%] max-w-4xl h-auto max-h-[92vh] sm:max-h-[90vh] my-4 sm:my-8 relative bg-neutral-50/50 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-neutral-300/20 backdrop-blur-[30px] overflow-hidden">
          {/* Header */}
          <div className="w-full px-4 sm:px-6 md:px-8 pt-4 sm:pt-6 pb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-gray-200">
            <div className="flex flex-col min-w-0">
              <h3 className={typography.h3} style={{ color: '#000000' }}>
                Document Details
              </h3>
              <p className="text-sm text-gray-600 mt-1 break-all">
                {doc?.docNumber || `FDOC-${doc?.fdoc_id?.substring(0, 8)?.toUpperCase() || 'N/A'}`}
              </p>
            </div>
            {/* Close Button */}
            <div className="self-end sm:self-auto flex-shrink-0">
              <IconButton
                onClick={onClose}
                icon={
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
              />
            </div>
          </div>

          {/* Dialog Content */}
          <div className="px-4 sm:px-6 md:px-8 pb-6 pt-4 sm:pt-6 space-y-6 overflow-y-auto max-h-[calc(92vh-120px)] sm:max-h-[calc(90vh-120px)]">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-green-600 mb-4" />
                <p className="text-gray-600">Loading document details...</p>
              </div>
            ) : error && !fullDocument ? (
              <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-800">Error</p>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            ) : (
              <>
                {/* Basic Information */}
                <Card.Gradient className="p-4 sm:p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Basic Information
                  </h4>
                  <EsignStatus fdoc={doc} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {getBasicInfoFields(displayExtractedData).map((field) => {
                      const isDocumentTypeField = field.key === 'document_type';
                      const isDocumentDateField = field.key === 'document_date';
                      const displayValue = field.isDate && !isDocumentDateField
                        ? formatDate(field.value)
                        : isDocumentTypeField
                          ? (displayExtractedData?.type || field.value || '')
                          : formatFieldValue(field.key, field.value);
                      
                      const isStatusField = field.key === 'status';
                      const statusValue = doc?.status || doc?.apiStatus || 'N/A';
                      
                      return (
                        <div key={field.key}>
                          <label className="text-sm font-semibold text-gray-600 mb-1 block flex items-center gap-1">
                            {field.key.includes('date') || field.key.includes('_at') ? (
                              <Calendar className="w-4 h-4" />
                            ) : null}
                            {field.label}
                          </label>
                          {isDocumentTypeField && field.isEditable ? (
                            <select
                              value={displayExtractedData?.type || ''}
                              onChange={isSnapshotView ? undefined : (e) => handleExtractedDataChange('type', e.target.value)}
                              disabled={isSnapshotView}
                              className={`w-full px-3 py-2 text-base border rounded-lg focus:outline-none focus:ring-2 ${
                                isSnapshotView
                                  ? 'text-gray-700 bg-gray-100 border-gray-200'
                                  : 'text-gray-900 bg-white border-gray-200 focus:border-green-500 focus:ring-green-100'
                              }`}
                            >
                              <option value="">Select Document Type</option>
                              <option value="PO">PO</option>
                              <option value="Invoice">Invoice</option>
                              <option value="Estimation">Estimation</option>
                              <option value="Bill">Bill</option>
                              <option value="Reception">Reception</option>
                            </select>
                          ) : isDocumentDateField && field.isEditable ? (
                            <input
                              type="date"
                              value={displayExtractedData?.date || ''}
                              onChange={isSnapshotView ? undefined : (e) => handleExtractedDataChange('date', e.target.value)}
                              disabled={isSnapshotView}
                              className={`w-full px-3 py-2 text-base border rounded-lg focus:outline-none focus:ring-2 ${
                                isSnapshotView
                                  ? 'text-gray-700 bg-gray-100 border-gray-200'
                                  : 'text-gray-900 bg-white border-gray-200 focus:border-green-500 focus:ring-green-100'
                              }`}
                            />
                          ) : (
                            <input
                              type="text"
                              value={displayValue}
                              readOnly
                              className={`w-full px-3 py-2 text-base rounded-lg border focus:outline-none focus:ring-2 ${
                                isStatusField
                                  ? statusValue === 'Active' || statusValue === 'Completed'
                                    ? 'bg-green-100 text-green-700 border-green-200 focus:border-green-500 focus:ring-green-100'
                                    : statusValue === 'Processing'
                                      ? 'bg-blue-100 text-blue-700 border-blue-200 focus:border-blue-500 focus:ring-blue-100'
                                      : statusValue === 'Failed'
                                        ? 'bg-red-100 text-red-700 border-red-200 focus:border-red-500 focus:ring-red-100'
                                        : 'bg-yellow-100 text-yellow-700 border-yellow-200 focus:border-yellow-500 focus:ring-yellow-100'
                                  : field.key === 'fdoc_id'
                                    ? 'text-sm text-gray-900 bg-white border border-gray-200 font-mono break-all focus:border-green-500 focus:ring-green-100'
                                    : 'text-gray-900 bg-white border border-gray-200 focus:border-green-500 focus:ring-green-100'
                              } ${field.key === 'file_name' ? 'break-words' : ''}`}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {getRelevantExtractedFields().length > 0 && (
                    <div className="mt-6">
                      <h5 className="text-base font-semibold text-gray-900 mb-3">
                        Extracted Details
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {getRelevantExtractedFields().map(([fieldKey, fieldValue]) => {
                          const inputValue = getEditableFieldValue(fieldValue);

                          return (
                            <div key={fieldKey}>
                              <label className="text-sm font-semibold text-gray-600 mb-1 block">
                                {getFieldDisplayLabel(fieldKey)}
                              </label>
                              <input
                                type="text"
                                value={inputValue}
                                onChange={
                                  isSnapshotView
                                    ? undefined
                                    : (e) =>
                                        handleExtractedDataChange(
                                          `fields.${fieldKey}`,
                                          parseEditedFieldValue(e.target.value, fieldValue)
                                        )
                                }
                                disabled={isSnapshotView}
                                className={`w-full px-3 py-2 text-base border rounded-lg focus:outline-none focus:ring-2 ${
                                  isSnapshotView
                                    ? 'text-gray-700 bg-gray-100 border-gray-200'
                                    : 'text-gray-900 bg-white border-gray-200 focus:border-green-500 focus:ring-green-100'
                                }`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Version History */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <h5 className="text-base font-semibold text-gray-900">Version History</h5>
                      {isSnapshotView ? (
                        <PrimaryButton
                          onClick={backToLatest}
                          disabled={snapshotLoading || isLoading || isSubmitting || isExtracting}
                        >
                          Back to Latest
                        </PrimaryButton>
                      ) : null}
                    </div>

                    {snapshotLoading && (
                      <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading version snapshot…
                      </div>
                    )}
                    {snapshotError && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700">{snapshotError}</p>
                      </div>
                    )}

                    {versionsError && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700">{versionsError}</p>
                      </div>
                    )}

                    <div className="mt-3 space-y-2">
                      {(versions || []).length === 0 && !versionsLoading ? (
                        <p className="text-sm text-gray-600">No versions yet. Versions appear after confirm.</p>
                      ) : null}

                      {(versions || []).map((v) => (
                        <button
                          key={v?.version_no}
                          type="button"
                          onClick={() => openSnapshot(v?.version_no)}
                          disabled={!v?.version_no || versionsLoading || snapshotLoading}
                          className="w-full text-left p-3 bg-white border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 disabled:opacity-60"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-3">
                            <p className="text-sm font-semibold text-gray-900">
                              Version #{v?.version_no}
                            </p>
                            <p className="text-xs text-gray-600">{v?.confirmed_at ? formatDate(v.confirmed_at) : ''}</p>
                          </div>
                          <p className="text-xs text-gray-700 mt-1">
                            {v?.status ? `Status: ${v.status}` : ''}
                            {v?.extracted_type ? ` • Type: ${v.extracted_type}` : ''}
                            {v?.extracted_date ? ` • Date: ${v.extracted_date}` : ''}
                          </p>
                        </button>
                      ))}
                    </div>

                    <div className="mt-3 flex items-center justify-end">
                      {versionsLastKey ? (
                        <PrimaryButton onClick={() => fetchVersions({ reset: false })} disabled={versionsLoading || snapshotLoading}>
                          {versionsLoading ? (
                            <span className="flex items-center justify-center gap-2">
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Loading…
                            </span>
                          ) : (
                            'Load more'
                          )}
                        </PrimaryButton>
                      ) : null}
                    </div>
                  </div>
                  
                  {/* Submit Button Section */}
                  <div className="mt-6 pt-6 border-t border-gray-200 flex flex-col items-center gap-4">
                    {submitSuccess && successMessage && (
                      <div className="w-full p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm font-semibold text-green-800 text-center">
                          {successMessage}
                        </p>
                      </div>
                    )}
                    {error && !isLoading && (
                      <div className="w-full p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700 text-center">{error}</p>
                      </div>
                    )}
                    {/* E-sign opt-in (frontend only sends boolean + optional signatory_id) */}
                    {!isSnapshotView &&
                      doc?.status !== 'confirmed' &&
                      doc?.apiStatus !== 'confirmed' &&
                      Boolean(extractedData) && (
                      <div className="w-full max-w-md">
                        <label className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={applyEsign}
                            onChange={(e) => setApplyEsign(e.target.checked)}
                            disabled={isSubmitting || isLoading || isExtracting}
                          />
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              Add authorised e-signature to this doc
                            </p>
                           
                          </div>
                        </label>
                      </div>
                    )}
                    <div className="w-full max-w-md flex flex-col sm:flex-row gap-3">
                      {/* Hide all actions in historical snapshot view */}
                      {isSnapshotView ? (
                        <div className="w-full p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
                          <p className="text-sm font-semibold text-gray-800">Viewing a historical snapshot (read-only)</p>
                          <p className="text-sm text-gray-600 mt-1">Use “Back to Latest” to edit and create a new revision.</p>
                        </div>
                      ) : (
                        <>
                          <PrimaryButton
                            onClick={handleExtract}
                            disabled={
                              isExtracting || 
                              isLoading || 
                              isSubmitting || 
                              doc?.status === 'extracted' || 
                              doc?.apiStatus === 'extracted' ||
                              doc?.status === 'confirmed' ||
                              doc?.apiStatus === 'confirmed'
                            }
                            className="flex-1"
                          >
                            {isExtracting ? (
                              <span className="flex items-center justify-center gap-2">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Extracting...
                              </span>
                            ) : (
                              'Extract Document'
                            )}
                          </PrimaryButton>
                          <PrimaryButton
                            onClick={handleSubmit}
                            disabled={
                              isSubmitting || 
                              isLoading || 
                              isExtracting || 
                              !extractedData
                            }
                            className="flex-1"
                          >
                            {isSubmitting ? (
                              <span className="flex items-center justify-center gap-2">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Saving...
                              </span>
                            ) : (
                              (doc?.status === 'confirmed' || doc?.apiStatus === 'confirmed')
                                ? 'Create Revision'
                                : 'Confirm Document'
                            )}
                          </PrimaryButton>
                        </>
                      )}
                    </div>
                  </div>
                </Card.Gradient>

              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FdocInPreview;

